/**
 * RBAC - Role-Based Access Control + Module-Based Access
 * Système de permissions pour le frontend
 */

// Rôles "niveau admin" (admin et admin_entrepot)
export const ADMIN_LEVEL_ROLES = ['admin', 'admin_entrepot'];

/**
 * Retourne true pour admin OU admin_entrepot
 * À utiliser pour les actions de gestion d'opérations (édition rapports/livraisons, suivi)
 */
export function isAdminLevel(user) {
  if (!user) return false;
  return ADMIN_LEVEL_ROLES.includes(user.role);
}

/**
 * Retourne true uniquement pour le rôle admin
 * À utiliser pour : gestion utilisateurs, clients, sites, facturation
 */
export function isFullAdmin(user) {
  return user?.role === 'admin';
}

// Définition des permissions par rôle
export const ROLE_PERMISSIONS = {
  admin: {
    canViewDashboard: true,
    canViewReports: true,
    canCreateReports: true,
    canEditReports: true,
    canDeleteReports: true,
    canViewDeliveries: true,
    canCreateDeliveries: true,
    canEditDeliveries: true,
    canDeleteDeliveries: true,
    canViewHistory: true,
    canManageUsers: true,
    canManageClients: true,
    canManageSites: true,
    canViewAllSites: true,
    canViewInvoice: true,
  },
  admin_entrepot: {
    canViewDashboard: true,
    canViewReports: true,
    canCreateReports: true,
    canEditReports: true,
    canDeleteReports: false,
    canViewDeliveries: true,
    canCreateDeliveries: true,
    canEditDeliveries: true,
    canDeleteDeliveries: false,
    canViewHistory: true,
    canManageUsers: false,
    canManageClients: false,
    canManageSites: false,
    canViewAllSites: true,
    canViewInvoice: false,
  },
  technicien: {
    canViewDashboard: true,
    canViewReports: true,
    canCreateReports: true,
    canEditReports: true,
    canDeleteReports: false,
    canViewDeliveries: true,
    canCreateDeliveries: false,
    canEditDeliveries: false,
    canDeleteDeliveries: false,
    canViewHistory: true,
    canManageUsers: false,
    canManageClients: false,
    canManageSites: false,
    canViewAllSites: false,
  },
  livreur: {
    canViewDashboard: true,
    canViewReports: true,
    canCreateReports: true,
    canEditReports: false,
    canDeleteReports: false,
    canViewDeliveries: true,
    canCreateDeliveries: true,
    canEditDeliveries: true,
    canDeleteDeliveries: false,
    canViewHistory: true,
    canManageUsers: false,
    canManageClients: false,
    canManageSites: false,
    canViewAllSites: false,
  },
  client: {
    canViewDashboard: true,
    canViewReports: true,
    canCreateReports: false,
    canEditReports: false,
    canDeleteReports: false,
    canViewDeliveries: true,
    canCreateDeliveries: false,
    canEditDeliveries: false,
    canDeleteDeliveries: false,
    canViewHistory: true,
    canManageUsers: false,
    canManageClients: false,
    canManageSites: false,
    canViewAllSites: false,
  },
  vendeur: {
    canViewDashboard: true,
    canViewReports: true,
    canCreateReports: true,
    canEditReports: true,
    canDeleteReports: false,
    canViewDeliveries: true,
    canCreateDeliveries: true,
    canEditDeliveries: true,
    canDeleteDeliveries: false,
    canViewHistory: true,
    canManageUsers: true,
    canManageClients: true,
    canManageSites: true,
    canViewAllSites: true,
    canViewInvoice: false,
  },
};

// Mapping: module_id -> routes autorisées
const MODULE_ROUTES = {
  suivi_aiguisage: [
    '/sharpening-menu',
    '/sharpening-tracking',
    '/sharpening-history',
    '/new-tracking',
  ],
  ajout_trepan: [
    '/sharpening-menu',
    '/add-drill-bit',
  ],
  ramassage: [
    '/delivery',
    '/delivery/pickup',
  ],
  livraison: [
    '/delivery',
    '/delivery/dropoff',
    '/delivery/confirm',
    '/delivery/history',
  ],
};

// Mapping: module_id -> nav items
const MODULE_NAV_ITEMS = {
  suivi_aiguisage: { name: 'Aiguisage', path: '/sharpening-menu', icon: 'Tool' },
  ajout_trepan: { name: 'Aiguisage', path: '/sharpening-menu', icon: 'Tool' },
  ramassage: { name: 'Livraison', path: '/delivery', icon: 'Truck' },
  livraison: { name: 'Livraison', path: '/delivery', icon: 'Truck' },
};

/**
 * Vérifie si l'utilisateur a une permission spécifique
 */
export function hasPermission(user, permission) {
  if (!user || !user.role) return false;
  const role = user.role || 'client';
  const permissions = ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.client;
  return permissions[permission] || false;
}

/**
 * Vérifie si l'utilisateur a l'un des rôles spécifiés
 */
export function hasRole(user, allowedRoles) {
  if (!user || !user.role) return false;
  return allowedRoles.includes(user.role);
}

/**
 * Vérifie si l'utilisateur a accès à un module spécifique
 */
export function hasModuleAccess(user, moduleId) {
  if (!user) return false;
  // Admin et admin_entrepot ont toujours accès à tout
  if (isAdminLevel(user)) return true;
  const modules = user.allowed_modules || [];
  return modules.includes(moduleId);
}

/**
 * Retourne la navigation autorisée pour l'utilisateur basée sur les modules
 */
export function getNavigationForUser(user) {
  if (!user) return [];

  // Items toujours visibles
  const baseNav = [
    { name: 'Accueil', path: '/dashboard', icon: 'Home' },
  ];

  // Admin: navigation complète
  if (user.role === 'admin') {
    return [
      ...baseNav,
      { name: 'Aiguisage', path: '/sharpening-menu', icon: 'Tool' },
      { name: 'Livraison', path: '/delivery', icon: 'Truck' },
      { name: 'Administration', path: '/administration', icon: 'Settings' },
      { name: 'Profil', path: '/profile', icon: 'User' },
    ];
  }

  // Admin entrepôt: tout sauf certaines sections de l'admin
  if (user.role === 'admin_entrepot') {
    return [
      ...baseNav,
      { name: 'Aiguisage', path: '/sharpening-menu', icon: 'Tool' },
      { name: 'Livraison', path: '/delivery', icon: 'Truck' },
      { name: 'Administration', path: '/administration', icon: 'Settings' },
      { name: 'Profil', path: '/profile', icon: 'User' },
    ];
  }

  const modules = user.allowed_modules || [];
  const addedPaths = new Set();

  // Ajouter les items de navigation basés sur les modules
  modules.forEach(moduleId => {
    const navItem = MODULE_NAV_ITEMS[moduleId];
    if (navItem && !addedPaths.has(navItem.path)) {
      baseNav.push(navItem);
      addedPaths.add(navItem.path);
    }
  });

  // Profil toujours en dernier
  baseNav.push({ name: 'Profil', path: '/profile', icon: 'User' });

  return baseNav;
}

// Backward compat - maintenant délègue à getNavigationForUser
export function getNavigationForRole(user) {
  return getNavigationForUser(user);
}

/**
 * Vérifie si l'utilisateur peut accéder à une route basée sur ses modules
 */
export function canAccessRoute(user, path) {
  if (!user) return false;

  // Admin et admin_entrepot ont accès à tout
  if (isAdminLevel(user)) return true;

  // Routes toujours accessibles
  const publicRoutes = ['/dashboard', '/home', '/profile', '/statistics'];
  if (publicRoutes.some(r => path.startsWith(r))) return true;

  // Vérifier les modules de l'utilisateur
  const modules = user.allowed_modules || [];
  if (modules.length === 0) return false; // Option B: aucun module = accès bloqué

  for (const moduleId of modules) {
    const routes = MODULE_ROUTES[moduleId] || [];
    if (routes.some(r => path.startsWith(r))) return true;
  }

  return false;
}

/**
 * Vérifie si l'utilisateur a accès à une route avec un module requis
 */
export function canAccessModule(user, requiredModules) {
  if (!user) return false;
  if (isAdminLevel(user)) return true;
  
  const userModules = user.allowed_modules || [];
  if (userModules.length === 0) return false;
  
  return requiredModules.some(m => userModules.includes(m));
}

/**
 * Détermine si une page doit être en lecture seule
 */
export function isReadOnly(user, context) {
  if (!user) return true;
  
  const role = user.role || 'client';
  
  if (role === 'client') return true;
  if (context === 'history') return !isAdminLevel(user);
  if (context === 'deliveries' && role === 'technicien') return true;
  
  return false;
}

/**
 * Messages d'erreur par défaut
 */
export const ACCESS_DENIED_MESSAGES = {
  default: "Vous n'avez pas l'accès pour cette action",
  create: "Vous n'avez pas la permission de créer cet élément",
  edit: "Vous n'avez pas la permission de modifier cet élément",
  delete: "Seuls les administrateurs peuvent supprimer des éléments",
  view: "Vous n'avez pas accès à cette page",
  site: "Vous n'avez pas accès à ce site",
  noModule: "Aucun module n'a été configuré pour votre compte. Contactez votre administrateur.",
};
